import { ChargeLine } from '@msdyn365-commerce/retail-proxy';

/**
 * Finds the header auto charge that CSU suppressed because the invoice account was already charged
 * during the current charge cycle.
 *
 * CSU zeroes the charge with OverrideChargeRequest rather than removing the line, so the suppressed
 * charge is identifiable from the cart itself and survives a cart refresh. Shipping lines are excluded
 * because the SHIP connector overrides those with the live rate, and a positive MarkupAutoLineRecordId
 * keeps this to auto charges rather than manually added ones.
 * @param chargeLines Header charge lines from the cart.
 * @returns The suppressed charge line, or undefined when nothing was suppressed.
 */
export const GetSuppressedOneTimePerDayCharge = (chargeLines: ChargeLine[] | undefined): ChargeLine | undefined => {
    return chargeLines?.find(chargeLine => {
        // Commerce records an override on both the flag and the override collection. Accept either, so
        // the message does not depend on which one Retail Server populates.
        const isOverridden = chargeLine.IsOverridden === true || (chargeLine.ChargeLineOverrides?.length ?? 0) > 0;

        return !chargeLine.IsShipping && isOverridden && chargeLine.CalculatedAmount === 0 && (chargeLine.MarkupAutoLineRecordId ?? 0) > 0;
    });
};

/**
 * Gets the customer facing name of a suppressed charge, falling back to the charge code.
 * @param chargeLine The suppressed charge line.
 * @returns The name to show, or undefined when neither a description nor a code is present.
 */
export const GetSuppressedOneTimePerDayChargeName = (chargeLine: ChargeLine | undefined): string | undefined => {
    if (!chargeLine) {
        return undefined;
    }

    const name = chargeLine.Description?.trim() || chargeLine.ChargeCode?.trim();

    return name ? name : undefined;
};
